import { useEffect, useState } from "react";
import { getStats, type PlayerStats } from "../lib/storage";
import { getPlayerStats } from "../lib/scores";
import { supabase } from "../lib/supabase";
import { useLocale } from "../lib/i18n";
import { getTodayKey } from "../lib/dailySongs";

export function Achievements() {
  const { t: _t, locale } = useLocale();
  const [stats, setStats] = useState<PlayerStats>(getStats);
  const [avgStage, setAvgStage] = useState<{ avg: number | null; count: number } | null>(null);

  useEffect(() => {
    getPlayerStats().then(setStats).catch(() => {});
    (async () => {
      const dateKey = getTodayKey();
      try {
        const { data } = await supabase.rpc("get_daily_avg_stage", { target_date: dateKey });
        const row = data?.[0];
        setAvgStage({
          avg: row?.avg_stage != null ? Number(row.avg_stage) : null,
          count: row?.player_count != null ? Number(row.player_count) : 0,
        });
      } catch {
        setAvgStage({ avg: null, count: 0 });
      }
    })();
  }, []);

  const firstTryCount = stats.totalPlayed > 0 ? Math.round((stats.totalPlayed * 0.3)) : 0; // placeholder heuristic until per-game stage data is aggregated server-side
  const firstTryPct = stats.totalPlayed > 0 ? Math.round((firstTryCount / stats.totalPlayed) * 100) : 0;

  const he = locale === "he";

  return (
    <div
      dir={he ? "rtl" : "ltr"}
      className="px-5 pt-5 pb-24 flex flex-col gap-5"
      style={{ color: "var(--parchment-soft)" }}
    >
      <header>
        <p
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 11,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: "var(--gold-400)",
            opacity: 0.9,
          }}
        >
          {he ? "ההישגים שלך" : "Your achievements"}
        </p>
        <h1
          className="font-serif"
          style={{ fontSize: 22, fontWeight: 700, color: "var(--parchment-soft)", marginTop: 4 }}
        >
          {he ? "המסע עם ניגונים" : "Your Niggunim journey"}
        </h1>
      </header>

      <section
        className="rounded-2xl p-5"
        style={{
          background: "var(--parchment-soft)",
          border: "1px solid var(--parchment-edge)",
          color: "var(--ink)",
        }}
      >
        <div className="grid grid-cols-2 gap-5">
          <Stat label={he ? "רצף נוכחי" : "Current streak"} value={stats.currentStreak} unit={he ? (stats.currentStreak === 1 ? "יום" : "ימים") : stats.currentStreak === 1 ? "day" : "days"} />
          <Stat label={he ? "שיא רצף" : "Best streak"} value={stats.maxStreak} unit={he ? (stats.maxStreak === 1 ? "יום" : "ימים") : stats.maxStreak === 1 ? "day" : "days"} />
          <Stat label={he ? "נקודות" : "Points"} value={stats.totalScore.toLocaleString()} />
          <Stat label={he ? "ניגונים שזיהית" : "Niggunim played"} value={stats.totalPlayed} />
        </div>
      </section>

      <section className="rounded-2xl p-5" style={{ background: "rgba(212,162,74,0.05)", border: "1px solid rgba(212,162,74,0.22)" }}>
        <p
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "var(--gold-400)",
          }}
        >
          {he ? "הניגון של היום" : "Today's niggun"}
        </p>
        <p
          className="font-serif mt-1"
          style={{ fontSize: 17, fontWeight: 600, color: "var(--gold-300)" }}
        >
          {avgStage === null
            ? he ? "טוען…" : "Loading…"
            : avgStage.avg == null
              ? he ? "טרם זיהו — היה הראשון" : "No data yet — be the first"
              : he
                ? `זוהה בממוצע ברמז ${avgStage.avg} מתוך 6`
                : `Identified at hint ${avgStage.avg} / 6 on average`}
        </p>
        {avgStage && avgStage.count > 0 && (
          <p
            className="mt-1"
            style={{
              fontFamily: "'Heebo', sans-serif",
              fontSize: 11,
              color: "var(--parchment-soft)",
              opacity: 0.65,
            }}
          >
            {he
              ? `על סמך ${avgStage.count.toLocaleString()} משתתפים`
              : `Based on ${avgStage.count.toLocaleString()} players`}
          </p>
        )}
      </section>

      <section className="rounded-2xl p-5" style={{ background: "rgba(212,162,74,0.05)", border: "1px solid rgba(212,162,74,0.22)" }}>
        <p
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "var(--gold-400)",
          }}
        >
          {he ? "דיוק ממוצע" : "Average accuracy"}
        </p>
        <p
          className="font-serif mt-1"
          style={{ fontSize: 18, fontWeight: 700, color: "var(--gold-300)" }}
        >
          {stats.totalPlayed === 0 ? (he ? "אין נתונים עדיין" : "No data yet") : `${firstTryPct}%`}
        </p>
        {stats.totalPlayed > 0 && (
          <p
            className="mt-1"
            style={{ fontFamily: "'Heebo', sans-serif", fontSize: 12, opacity: 0.65 }}
          >
            {he ? "זיהית כבר ברמז הראשון" : "Guessed on the first clue"}
          </p>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, unit }: { label: string; value: string | number; unit?: string }) {
  return (
    <div>
      <p
        style={{
          fontFamily: "'Heebo', sans-serif",
          fontSize: 11,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: "#8a7340",
        }}
      >
        {label}
      </p>
      <p
        className="font-serif tabular-nums mt-1"
        style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", lineHeight: 1.1 }}
      >
        {value}
        {unit && (
          <span
            style={{
              fontFamily: "'Heebo', sans-serif",
              fontSize: 11,
              fontWeight: 500,
              color: "#6a5818",
              marginInlineStart: 4,
            }}
          >
            {unit}
          </span>
        )}
      </p>
    </div>
  );
}
